import { useRef, useEffect, useState, useMemo, useCallback, type CSSProperties } from "react";
import { motion, useInView, useMotionValue, useSpring } from "framer-motion";
import { useReducedMotion } from "../../hooks/useReducedMotion";
import { usePretextLayout } from "../../hooks/usePretextLayout";
import { toFontSpec } from "../../utils/fontSpec";

type Mode = "scatter" | "wave" | "cascade" | "slide" | "magnetic" | "scroll-scrub";

interface PretextRevealProps {
  children: string;
  mode: Mode;
  as?: "h1" | "h2" | "h3" | "h4" | "p" | "span" | "div";
  className?: string;
  style?: CSSProperties;
  delay?: number;
  once?: boolean;
  splitBy?: "word" | "line";
  intensity?: number;
  scrubStart?: string;
  scrubEnd?: string;
  idle?: "float" | "none";
  hoverMode?: "magnetic" | "none";
}

const EASING: [number, number, number, number] = [0.25, 0.46, 0.45, 0.94];
const isMobile = () => typeof window !== "undefined" && window.innerWidth < 768;
const isTouch = () => typeof window !== "undefined" && ("ontouchstart" in window || navigator.maxTouchPoints > 0);

/** Deterministic pseudo-random from seed */
function seededRandom(seed: number): number {
  const x = Math.sin(seed * 9301 + 49297) * 49297;
  return x - Math.floor(x);
}

/* ═══ Scatter Mode ═══ */
function ScatterWords({
  words, delay, intensity, idle,
}: {
  words: string[]; delay: number; intensity: number; idle: boolean;
}) {
  const mobile = isMobile();
  const range = mobile ? 120 : 300;
  const yRange = mobile ? 80 : 200;

  return (
    <>
      {words.map((word, i) => {
        const seed = i * 7 + word.charCodeAt(0);
        const rx = (seededRandom(seed) - 0.5) * 2 * range * intensity;
        const ry = (seededRandom(seed + 1) - 0.5) * 2 * yRange * intensity;
        const rr = (seededRandom(seed + 2) - 0.5) * 30 * intensity;

        return (
          <motion.span
            key={i}
            initial={{ x: rx, y: ry, opacity: 0, rotate: rr, scale: 0.8 }}
            animate={{ x: 0, y: 0, opacity: 1, rotate: 0, scale: 1 }}
            transition={{
              type: "spring",
              stiffness: 80,
              damping: 12,
              mass: 0.8,
              delay: delay + i * 0.04,
            }}
            style={{ display: "inline-block", marginRight: "0.3em", willChange: "transform" }}
          >
            {idle ? (
              <motion.span
                animate={{ y: [0, -2, 0, 2, 0] }}
                transition={{
                  duration: 3 + i * 0.2,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: delay + i * 0.04 + 1.2,
                }}
                style={{ display: "inline-block" }}
              >
                {word}
              </motion.span>
            ) : (
              word
            )}
          </motion.span>
        );
      })}
    </>
  );
}

/* ═══ Wave Mode ═══ */
function WaveWords({ words, delay, intensity }: { words: string[]; delay: number; intensity: number }) {
  return (
    <>
      {words.map((word, i) => (
        <motion.span
          key={i}
          initial={{ y: 30 * intensity, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{
            duration: 0.6,
            ease: EASING,
            delay: delay + i * 0.06 + Math.sin(i * 0.8) * 0.04,
          }}
          style={{ display: "inline-block", marginRight: "0.3em" }}
        >
          {word}
        </motion.span>
      ))}
    </>
  );
}

/* ═══ Cascade Mode ═══ */
function CascadeWords({ words, delay, intensity }: { words: string[]; delay: number; intensity: number }) {
  return (
    <span style={{ perspective: 600 }}>
      {words.map((word, i) => (
        <span key={i} style={{ overflow: "hidden", display: "inline-block", marginRight: "0.3em" }}>
          <motion.span
            initial={{ y: -60 * intensity, opacity: 0, rotateX: -45 }}
            animate={{ y: 0, opacity: 1, rotateX: 0 }}
            transition={{
              type: "spring",
              stiffness: 120,
              damping: 14,
              mass: 1,
              delay: delay + i * 0.07,
            }}
            style={{ display: "inline-block", willChange: "transform" }}
          >
            {word}
          </motion.span>
        </span>
      ))}
    </span>
  );
}

/* ═══ Slide Mode (per-line) ═══ */
function SlideLines({
  lines, delay,
}: {
  lines: { text: string; width: number }[]; delay: number;
}) {
  return (
    <>
      {lines.map((line, i) => (
        <div key={i} style={{ overflow: "hidden" }}>
          <motion.div
            initial={{ x: i % 2 === 0 ? "-100%" : "100%", opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{
              duration: 0.8,
              ease: [0.77, 0, 0.175, 1] as [number, number, number, number],
              delay: delay + i * 0.12,
            }}
          >
            {line.text}
          </motion.div>
        </div>
      ))}
    </>
  );
}

/* ═══ Magnetic Mode ═══ */
function MagneticWords({
  words, intensity,
}: {
  words: string[]; intensity: number;
}) {
  const containerRef = useRef<HTMLSpanElement>(null);
  const [mouseLocal, setMouseLocal] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (isTouch()) return;
    const el = containerRef.current;
    if (!el) return;

    const onMove = (e: MouseEvent) => {
      const rect = el.getBoundingClientRect();
      setMouseLocal({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  return (
    <span ref={containerRef} style={{ display: "inline-flex", flexWrap: "wrap", gap: "0 0.3em" }}>
      {words.map((word, i) => (
        <MagneticWord key={i} word={word} index={i} mouseLocal={mouseLocal} intensity={intensity} />
      ))}
    </span>
  );
}

function MagneticWord({
  word, index, mouseLocal, intensity,
}: {
  word: string; index: number; mouseLocal: { x: number; y: number }; intensity: number;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const springX = useSpring(x, { stiffness: 400, damping: 15 });
  const springY = useSpring(y, { stiffness: 400, damping: 15 });

  useEffect(() => {
    if (!ref.current || isTouch()) return;
    const rect = ref.current.getBoundingClientRect();
    const parent = ref.current.parentElement?.getBoundingClientRect();
    if (!parent) return;

    const cx = rect.left - parent.left + rect.width / 2;
    const cy = rect.top - parent.top + rect.height / 2;
    const dx = mouseLocal.x - cx;
    const dy = mouseLocal.y - cy;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const radius = 150;

    if (dist < radius) {
      const force = (1 - dist / radius) * 8 * intensity;
      x.set(-dx / dist * force);
      y.set(-dy / dist * force);
    } else {
      x.set(0);
      y.set(0);
    }
  }, [mouseLocal, intensity, x, y]);

  return (
    <motion.span
      ref={ref}
      style={{ display: "inline-block", x: springX, y: springY }}
    >
      {word}
    </motion.span>
  );
}

/* ═══ Scroll-Scrub Mode ═══ */
function ScrollScrubWords({
  words, intensity, scrubStart, scrubEnd,
}: {
  words: string[]; intensity: number; scrubStart: string; scrubEnd: string;
}) {
  const containerRef = useRef<HTMLSpanElement>(null);
  const wordRefs = useRef<(HTMLSpanElement | null)[]>([]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || wordRefs.current.length === 0) return;

    let cleanup: (() => void) | undefined;

    (async () => {
      const gsapModule = await import("gsap");
      const { ScrollTrigger } = await import("gsap/ScrollTrigger");
      const gsap = gsapModule.default || gsapModule.gsap;
      gsap.registerPlugin(ScrollTrigger);

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: el,
          start: scrubStart,
          end: scrubEnd,
          scrub: 0.8,
        },
      });

      wordRefs.current.forEach((wordEl, i) => {
        if (!wordEl) return;
        gsap.set(wordEl, { opacity: 0.15, y: 8 * intensity });
        tl.to(wordEl, { opacity: 1, y: 0, duration: 0.3 }, i * 0.05);
      });

      cleanup = () => {
        tl.kill();
        ScrollTrigger.getAll().forEach((st) => {
          if (st.trigger === el) st.kill();
        });
      };
    })();

    return () => cleanup?.();
  }, [words, intensity, scrubStart, scrubEnd]);

  return (
    <span ref={containerRef} style={{ display: "inline-flex", flexWrap: "wrap", gap: "0 0.3em" }}>
      {words.map((word, i) => (
        <span
          key={i}
          ref={(el) => { wordRefs.current[i] = el; }}
          style={{ display: "inline-block", opacity: 0.15 }}
        >
          {word}
        </span>
      ))}
    </span>
  );
}

/* ═══ Main PretextReveal Component ═══ */
export default function PretextReveal({
  children,
  mode,
  as: Tag = "div",
  className,
  style,
  delay = 0,
  once = true,
  splitBy,
  intensity = 1,
  scrubStart = "top 80%",
  scrubEnd = "bottom 20%",
  idle = "none",
  hoverMode = "none",
}: PretextRevealProps) {
  const reducedMotion = useReducedMotion();
  const fontSpec = toFontSpec(style || {});
  const lineHeight = typeof style?.lineHeight === "number" ? style.lineHeight : 1.2;
  const { lines, ready, containerRef } = usePretextLayout(children, fontSpec, lineHeight);

  const ref = useRef<HTMLElement>(null);
  const inView = useInView(ref as React.RefObject<Element>, { once, amount: 0.15 });
  const [entranceDone, setEntranceDone] = useState(false);

  // Merge refs
  const setRefs = useCallback((el: HTMLElement | null) => {
    (ref as React.MutableRefObject<HTMLElement | null>).current = el;
    (containerRef as React.MutableRefObject<HTMLElement | null>).current = el;
  }, [containerRef]);

  // Split text into words
  const words = useMemo(() => children.split(/\s+/).filter(Boolean), [children]);

  // Determine effective splitBy
  const effectiveSplit = splitBy || (mode === "slide" ? "line" : "word");

  // After entrance animation — switch to hover mode if set
  useEffect(() => {
    if (inView && mode !== "magnetic" && mode !== "scroll-scrub") {
      const totalDuration = delay + words.length * 0.07 + 0.8; // rough estimate
      const timer = setTimeout(() => setEntranceDone(true), totalDuration * 1000);
      return () => clearTimeout(timer);
    }
  }, [inView, delay, words.length, mode]);

  // Reduced motion: render plain text
  if (reducedMotion) {
    return (
      <Tag ref={setRefs} className={className} style={style}>
        {children}
      </Tag>
    );
  }

  // Not in view and not scroll-scrub: render invisible placeholder
  const isScrollScrub = mode === "scroll-scrub";

  // Post-entrance magnetic hover
  if (entranceDone && hoverMode === "magnetic" && !isTouch() && !isMobile()) {
    return (
      <Tag ref={setRefs} className={className} style={style}>
        <MagneticWords words={words} intensity={intensity} />
      </Tag>
    );
  }

  const content = (() => {
    if (isScrollScrub) {
      return (
        <ScrollScrubWords
          words={words}
          intensity={intensity}
          scrubStart={scrubStart}
          scrubEnd={scrubEnd}
        />
      );
    }

    if (!inView) return <span style={{ opacity: 0 }}>{children}</span>;

    switch (mode) {
      case "scatter":
        return <ScatterWords words={words} delay={delay} intensity={intensity} idle={idle === "float"} />;
      case "wave":
        return <WaveWords words={words} delay={delay} intensity={intensity} />;
      case "cascade":
        return <CascadeWords words={words} delay={delay} intensity={intensity} />;
      case "slide":
        return effectiveSplit === "line" && ready
          ? <SlideLines lines={lines} delay={delay} />
          : <SlideLines lines={[{ text: children, width: 0 }]} delay={delay} />;
      case "magnetic":
        return <MagneticWords words={words} intensity={intensity} />;
      default:
        return <>{children}</>;
    }
  })();

  return (
    <Tag ref={setRefs} className={className} style={style}>
      {content}
    </Tag>
  );
}
